"use client";
import { STORAGE_KEY } from "@/app/utils/services/service/cookieService";
import React, { useEffect, useState } from "react";
import { CookieIcon } from "../Icons";
import { Modal } from "../modal";
import { useTranslations } from "next-intl";

type CookieSettings = {
  requiredCookies: boolean;
  maps: boolean;
};

type Props = {
  openCookieModal?: boolean;
  page?: string;
  callBackFn?: () => void;
};

const DEFAULT_SETTINGS: CookieSettings = {
  requiredCookies: true,
  maps: false,
};

export default function CookieDialog({
  page,
  callBackFn,
  openCookieModal = false,
}: Props) {
  const t = useTranslations();
  const [settings, setSettings] = useState<CookieSettings>(DEFAULT_SETTINGS);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (!page) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          setSettings(JSON.parse(saved));
        } else {
          setOpenModal(true);
        }
      } catch {
        setOpenModal(true);
      }
    }
  }, [page]);

  const saveSettings = (newSettings: CookieSettings) => {
    setSettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
    if (callBackFn) {
      callBackFn();
    } else {
      setOpenModal(false);
    }
  };

  const acceptAll = () => saveSettings({ requiredCookies: true, maps: true });
  const rejectAll = () => saveSettings({ requiredCookies: true, maps: false });
  const saveSelection = () =>
    saveSettings({ requiredCookies: true, maps: settings.maps });

  return (
    <Modal
      isOpen={openModal || openCookieModal}
      width="w-full 2xl:w-[30%] lg:w-[40%] sm:w-2/3 !rounded-2xl"
      height="p-6"
      outerClassName="overflow-y-scroll"
      innerClassName="m-0.5 rounded-2xl p-0"
      bgColor="bg-lightwhite dark:bg-sleap-gray-950"
    >
      <div className="flex flex-col gap-8 rounded-2xl bg-lightwhite dark:bg-sleap-gray-950 dark:text-lightwhite text-sleap-gray-950">
        <div className="flex flex-col sm:gap-6 gap-4">
          <div className="flex items-center gap-4">
            <div id="cookie-icon" className="h-8 w-8">
              <CookieIcon />
            </div>
            <h3 className="text-2xl mb-0">{t("Other.cookies")}</h3>
          </div>
          <p className="mb-0 sm:text-base text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
            {t("Other.weOnlyUseDataWithYourConsent")}
          </p>
          <div className="flex-col space-y-4">
            <div>
              <label className="flex items-center gap-2 font-semibold text-base">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-sleap-purple-300 rounded-sm border-2 cursor-pointer"
                  checked
                  readOnly
                />
                {t("Other.required")}
              </label>
              <p className="ms-6 text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
                {t("Other.theseCookiesAreNecessary")}
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 font-semibold text-base">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-sleap-purple-300 rounded-sm border-2 cursor-pointer"
                  checked={settings.maps}
                  onChange={() => setSettings((s) => ({ ...s, maps: !s.maps }))}
                />
                {t("Other.maps")}
              </label>
              <p className="ms-6 text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
                {t("Other.theseCookiesAreNecessaryForMap")}
              </p>
            </div>
            <p className="sm:text-base text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
              {t("Other.forMoreInformationSee")}{" "}
              {page !== "privacy" && (
                <a href="/privacy-policy">{t("Other.privacyPolicy")}</a>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-between">
          <button
            onClick={acceptAll}
            className="bg-sleap-purple-300 md:px-4 px-3 py-2 rounded-full w-full sm:hidden text-white"
          >
            <h5 className="text-sm font-medium mb-0 font-switzer">
              {t("Other.acceptAll")}
            </h5>
          </button>
          <div className="flex gap-2 w-full">
            <button
              onClick={rejectAll}
              className="border border-sleap-gray-400 md:px-4 px-3 py-2 rounded-full flex-1 flex justify-center dark:text-lightwhite text-sleap-gray-950"
            >
              <h5 className="text-sm font-medium mb-0 font-switzer">
                {t("Other.rejectAll")}
              </h5>
            </button>
            <button
              onClick={saveSelection}
              className="border dark:border-sleap-gray-600 border-sleap-gray-100 md:px-4 px-3 py-2 rounded-full flex-1 flex justify-center dark:text-lightwhite text-sleap-gray-950 bg-sleap-gray-200 dark:bg-sleap-gray-700"
            >
              <h5 className="text-sm font-medium mb-0 font-switzer">
                {t("Other.saveSelection")}
              </h5>
            </button>
            <button
              onClick={acceptAll}
              className="bg-sleap-purple-300 md:px-4 px-3 py-2 rounded-full hidden sm:flex justify-center text-lightwhite"
            >
              <h5 className="text-sm font-medium mb-0 font-switzer">
                {t("Other.acceptAll")}
              </h5>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
